import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDateDE } from "@/lib/playerConstants";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Save, Trash2, Plus, Pencil, ChevronDown, ChevronUp, Eye,
  Library, BookmarkPlus,
} from "lucide-react";
import { toast } from "sonner";
import FormPickerDialog from "@/components/FormPickerDialog";
import FormEditorDialog from "@/components/FormEditorDialog";
import SaveFormDialog from "@/components/SaveFormDialog";
import ImageField from "@/components/ImageField";
import AutoTextarea from "@/components/AutoTextarea";
import { FORM_TYPE_LABEL, FORM_TYPE_ACCENT, BLOCK_DEFAULT_FORM_TYPE } from "@/lib/formTypes";

const BLOCK_META = [
  { key: "aktivierung",   label: "Aktivierung / Warm-Up" },
  { key: "spielform_1",   label: "Spielform 1" },
  { key: "zwischenblock", label: "Zwischenblock / Technik" },
  { key: "spielform_2",   label: "Spielform 2" },
];

const emptyBlock = (key) => ({
  key, uebung: "", form_type: null, focus_area_ids: [], source_form_id: null,
  dauer_min: null, spieler_von: null, spieler_bis: null,
  organisation: "", material: "", ablauf: "",
  variationen: "", coaching: "", tipps: "", fotos: [],
});

export default function TrainingSession() {
  const { id: trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [focusAreas, setFocusAreas] = useState([]);
  const [name, setName] = useState("");
  const [focusIds, setFocusIds] = useState([]);
  const [blocks, setBlocks] = useState(BLOCK_META.map((m) => emptyBlock(m.key)));
  const [sessionId, setSessionId] = useState(null);
  const [openBlock, setOpenBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [faDialog, setFaDialog] = useState(null); // {mode:'create'|'edit', item?}
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(false);
  const [pickerBlock, setPickerBlock] = useState(null);   // idx | null
  const [saveBlock, setSaveBlock] = useState(null);       // idx | null
  const [newFormPrefill, setNewFormPrefill] = useState(null); // {prefill, idx} | null

  useEffect(() => {
    (async () => {
      try {
        const [tRes, sRes, faRes] = await Promise.all([
          api.get(`/trainings/${trainingId}`),
          api.get(`/trainings/${trainingId}/session`),
          api.get("/focus-areas"),
        ]);
        setTraining(tRes.data);
        setFocusAreas(faRes.data);
        if (sRes.data) {
          setSessionId(sRes.data.id);
          setName(sRes.data.name || "");
          setFocusIds(sRes.data.focus_area_ids || []);
          const map = Object.fromEntries((sRes.data.blocks || []).map((b) => [b.key, b]));
          setBlocks(BLOCK_META.map((m) => ({ ...emptyBlock(m.key), ...(map[m.key] || {}) })));
        }
      } catch {
        toast.error("Trainingstermin nicht gefunden");
        navigate("/training");
      } finally { setLoading(false); }
    })();
  }, [trainingId, navigate]);

  const setBlockField = (idx, field, value) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };

  const setBlockImage = (idx, dataUrl) => {
    setBlocks((prev) => prev.map((b, i) =>
      i === idx ? { ...b, fotos: dataUrl ? [dataUrl] : [] } : b
    ));
  };

  // Bibliotheksform 1:1 in einen Block kopieren (unabhängige Kopie, Referenz gemerkt).
  const applyForm = (idx, form) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? {
      ...b,
      uebung: form.name || "",
      form_type: form.form_type || null,
      focus_area_ids: form.focus_area_ids || [],
      source_form_id: form.id,
      dauer_min: form.dauer_min ?? null,
      spieler_von: form.spieler_von ?? null,
      spieler_bis: form.spieler_bis ?? null,
      organisation: form.organisation || "",
      material: form.material || "",
      ablauf: form.ablauf || "",
      variationen: form.variationen || "",
      coaching: form.coaching || "",
      tipps: form.tipps || "",
      fotos: form.fotos || [],
    } : b)));
    // Übernommene Schwerpunkte auch der Einheit hinzufügen (ohne Duplikate).
    if ((form.focus_area_ids || []).length) {
      setFocusIds((prev) => Array.from(new Set([...prev, ...form.focus_area_ids])));
    }
    setPickerBlock(null);
    toast.success("Form übernommen");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        focus_area_ids: focusIds,
        blocks: blocks.map((b) => ({
          ...b,
          dauer_min: b.dauer_min === "" ? null : b.dauer_min,
          spieler_von: b.spieler_von === "" ? null : b.spieler_von,
          spieler_bis: b.spieler_bis === "" ? null : b.spieler_bis,
        })),
      };
      const { data } = await api.put(`/trainings/${trainingId}/session`, payload);
      setSessionId(data.id);
      toast.success("Trainingseinheit gespeichert");
      navigate(`/training/${trainingId}/einheit/ansehen`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const deleteSession = async () => {
    try {
      await api.delete(`/trainings/${trainingId}/session`);
      toast.success("Trainingseinheit gelöscht");
      navigate(`/training/${trainingId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto p-6 text-zinc-500 text-sm">Lädt…</div>;

  return (
    <div className="w-full max-w-2xl lg:max-w-6xl xl:max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32" data-testid="ts-editor">
      <button onClick={() => navigate(`/training/${trainingId}`)}
        className="text-xs uppercase tracking-wider text-zinc-500 hover:text-brand-lighter transition flex items-center gap-1 mb-4"
        data-testid="ts-back">
        <ArrowLeft className="h-3 w-3" /> Trainingstermin
      </button>

      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-wider text-brand-lighter">Trainingseinheit</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
          {sessionId ? "Bearbeiten" : "Anlegen"}
        </h1>
        {training && (
          <div className="text-xs text-zinc-400 mt-1">{formatDateDE(training.datum)} · {training.uhrzeit}{training.ort ? " · " + training.ort : ""}</div>
        )}
      </div>

      {/* Allgemeine Angaben */}
      <div className="border border-zinc-800 bg-zinc-900/50 p-4 space-y-4 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-zinc-400">Allgemeine Angaben</div>
        <div>
          <Label className="text-xs text-zinc-300">Bezeichnung</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Passspiel-Training"
            className="bg-[#102A3D] border-[#1F4B6B] text-zinc-100 mt-1 h-11 focus-visible:ring-brand"
            data-testid="ts-name" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-zinc-300">Trainingsschwerpunkte</Label>
            <button type="button"
              onClick={() => setFaDialog({ mode: "create" })}
              className="text-[11px] uppercase tracking-wider text-brand-lighter hover:text-brand-light"
              data-testid="ts-fa-add">+ Neu</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="ts-fa-chips">
            {focusAreas.map((fa) => {
              const active = focusIds.includes(fa.id);
              return (
                <div key={fa.id} className="relative group">
                  <button type="button"
                    onClick={() => setFocusIds((prev) => active ? prev.filter((x) => x !== fa.id) : [...prev, fa.id])}
                    className={
                      "min-h-[36px] pr-7 pl-3 rounded-full border text-xs font-medium transition " +
                      (active
                        ? "bg-brand border-brand text-white"
                        : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60")
                    }
                    data-testid={`ts-fa-${fa.id}`}
                  >{fa.name}</button>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setFaDialog({ mode: "edit", item: fa }); }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 flex items-center justify-center"
                    data-testid={`ts-fa-edit-${fa.id}`}
                    aria-label="Bearbeiten"
                  ><Pencil className="h-3 w-3" /></button>
                </div>
              );
            })}
            {focusAreas.length === 0 && <div className="text-xs text-zinc-500">Noch keine Schwerpunkte.</div>}
          </div>
        </div>
      </div>

      {/* 4 Blöcke */}
      <div className="space-y-3">
        {BLOCK_META.map((meta, idx) => {
          const b = blocks[idx];
          const isOpen = openBlock === idx;
          const summary = b.uebung || (b.dauer_min ? `${b.dauer_min} min` : "leer");
          return (
            <div key={meta.key} className="border border-zinc-800 bg-zinc-900/50" data-testid={`ts-block-${idx}`}>
              <button type="button" onClick={() => setOpenBlock(isOpen ? null : idx)}
                className="w-full px-4 py-3 flex items-center justify-between gap-3"
                data-testid={`ts-block-toggle-${idx}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-display text-lg font-bold text-brand-lighter tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                  <div className="text-left min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{meta.label}</div>
                    <div className="flex items-center gap-2 min-w-0">
                      {b.form_type && (
                        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide font-bold border shrink-0 " + (FORM_TYPE_ACCENT[b.form_type] || "")}>
                          {FORM_TYPE_LABEL[b.form_type]}
                        </span>
                      )}
                      <div className="text-sm text-zinc-200 truncate">{summary}</div>
                    </div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-zinc-800">
                  <div className="flex flex-wrap gap-2 pt-3 mb-3">
                    <button type="button" onClick={() => setPickerBlock(idx)}
                      className="inline-flex items-center gap-1.5 min-h-[38px] px-3 rounded-md bg-brand hover:bg-brand-light text-white text-xs font-semibold transition"
                      data-testid={`ts-b${idx}-from-library`}>
                      <Library className="h-3.5 w-3.5" /> Aus Bibliothek
                    </button>
                    <button type="button" onClick={() => setSaveBlock(idx)}
                      disabled={!(b.uebung || "").trim()}
                      className="inline-flex items-center gap-1.5 min-h-[38px] px-3 rounded-md border border-zinc-700 bg-zinc-950 text-zinc-200 hover:border-brand-lighter/50 hover:text-brand-lighter text-xs font-semibold transition disabled:opacity-40"
                      data-testid={`ts-b${idx}-save-to-library`}>
                      <BookmarkPlus className="h-3.5 w-3.5" /> In Bibliothek speichern
                    </button>
                    {b.source_form_id && (
                      <span className="inline-flex items-center text-[10px] text-zinc-500 self-center">
                        aus Vorlage übernommen
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                    <div className="md:col-span-2">
                      <BlockField label="Bezeichnung der Übung" value={b.uebung}
                        onChange={(v) => setBlockField(idx, "uebung", v)} testId={`ts-b${idx}-uebung`} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <NumField label="Dauer (min)" value={b.dauer_min}
                        onChange={(v) => setBlockField(idx, "dauer_min", v)} testId={`ts-b${idx}-dauer`} />
                      <NumField label="Sp. von" value={b.spieler_von}
                        onChange={(v) => setBlockField(idx, "spieler_von", v)} testId={`ts-b${idx}-svon`} />
                      <NumField label="Sp. bis" value={b.spieler_bis}
                        onChange={(v) => setBlockField(idx, "spieler_bis", v)} testId={`ts-b${idx}-sbis`} />
                    </div>
                    <TextArea label="Benötigtes Material" value={b.material}
                      onChange={(v) => setBlockField(idx, "material", v)} testId={`ts-b${idx}-mat`} />
                    <div className="md:col-span-2">
                      <TextArea label="Organisation" value={b.organisation}
                        onChange={(v) => setBlockField(idx, "organisation", v)} testId={`ts-b${idx}-org`} />
                    </div>
                    <div className="md:col-span-2">
                      <TextArea label="Ablauf" value={b.ablauf} rows={4}
                        onChange={(v) => setBlockField(idx, "ablauf", v)} testId={`ts-b${idx}-abl`} />
                    </div>
                    <div className="md:col-span-2">
                      <TextArea label="Coachingpunkte" value={b.coaching} rows={3}
                        onChange={(v) => setBlockField(idx, "coaching", v)} testId={`ts-b${idx}-coach`} />
                    </div>
                    <TextArea label="Variationen" value={b.variationen} rows={3}
                      onChange={(v) => setBlockField(idx, "variationen", v)} testId={`ts-b${idx}-var`} />
                    <TextArea label="Tipps / Korrekturen" value={b.tipps} rows={3}
                      onChange={(v) => setBlockField(idx, "tipps", v)} testId={`ts-b${idx}-tipps`} />
                    <div className="md:col-span-2">
                      <ImageField
                        value={(b.fotos || [])[0] || null}
                        onChange={(d) => setBlockImage(idx, d)}
                        testId={`ts-b${idx}-image`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sessionId && (
        <button type="button"
          onClick={() => setConfirmDeleteSession(true)}
          className="mt-6 text-sm text-rose-400 hover:text-rose-300 flex items-center gap-1.5"
          data-testid="ts-delete-btn">
          <Trash2 className="h-4 w-4" /> Trainingseinheit löschen
        </button>
      )}

      {/* Sticky Save */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-zinc-950/95 border-t border-zinc-800 backdrop-blur p-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="w-full max-w-2xl lg:max-w-6xl xl:max-w-[1400px] mx-auto px-1 sm:px-6 lg:px-8 flex gap-2 lg:justify-end">
          {sessionId && (
            <Button variant="outline" onClick={() => navigate(`/training/${trainingId}/einheit/ansehen`)}
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 rounded-md h-11"
              data-testid="ts-view-btn"><Eye className="h-4 w-4" /></Button>
          )}
          <Button onClick={save} disabled={saving}
            className="flex-1 lg:flex-none lg:min-w-[260px] bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
            data-testid="ts-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </div>

      <FocusAreaDialog dialog={faDialog} onClose={() => setFaDialog(null)}
        onSaved={(updated) => {
          if (updated._deleted) {
            setFocusAreas((prev) => prev.filter((x) => x.id !== updated.id));
            setFocusIds((prev) => prev.filter((x) => x !== updated.id));
          } else {
            setFocusAreas((prev) => {
              const exists = prev.find((x) => x.id === updated.id);
              const next = exists ? prev.map((x) => x.id === updated.id ? updated : x) : [...prev, updated];
              return next.sort((a, b) => a.name.localeCompare(b.name));
            });
          }
          setFaDialog(null);
        }} />

      <AlertDialog open={confirmDeleteSession} onOpenChange={setConfirmDeleteSession}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Trainingseinheit löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Alle Blöcke inkl. Fotos gehen verloren. Der Trainingstermin bleibt bestehen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSession}
              className="bg-red-600 hover:bg-red-500 text-white" data-testid="ts-delete-confirm">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FormPickerDialog
        open={pickerBlock !== null}
        blockKey={pickerBlock !== null ? BLOCK_META[pickerBlock].key : null}
        blockLabel={pickerBlock !== null ? BLOCK_META[pickerBlock].label : ""}
        focusAreas={focusAreas}
        sessionFocusIds={focusIds}
        onClose={() => setPickerBlock(null)}
        onPick={(form) => applyForm(pickerBlock, form)}
      />

      <SaveFormDialog
        open={saveBlock !== null}
        block={saveBlock !== null ? blocks[saveBlock] : null}
        sourceName={saveBlock !== null ? (blocks[saveBlock].uebung || "") : ""}
        focusTypeDefault={saveBlock !== null ? BLOCK_DEFAULT_FORM_TYPE[BLOCK_META[saveBlock].key] : null}
        onClose={() => setSaveBlock(null)}
        onUpdated={() => setSaveBlock(null)}
        onSaveAsNew={(prefill) => {
          const idx = saveBlock;
          setSaveBlock(null);
          setNewFormPrefill({ prefill, idx });
        }}
      />

      <FormEditorDialog
        open={!!newFormPrefill}
        initial={newFormPrefill?.prefill}
        focusAreas={focusAreas}
        title="Als neue Form speichern"
        onClose={() => setNewFormPrefill(null)}
        onSaved={(form) => {
          // Block mit neuer Vorlage verknüpfen, damit später "aktualisieren" möglich ist.
          const idx = newFormPrefill?.idx;
          if (idx != null && form?.id) {
            setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, source_form_id: form.id, form_type: form.form_type } : b)));
          }
          setNewFormPrefill(null);
        }}
      />
    </div>
  );
}

function BlockField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="bg-[#102A3D] border-[#1F4B6B] text-zinc-100 mt-1 h-11 focus-visible:ring-brand"
        data-testid={testId} />
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</Label>
      <Input type="number" inputMode="numeric" value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="bg-[#102A3D] border-[#1F4B6B] text-zinc-100 mt-1 h-11 focus-visible:ring-brand"
        data-testid={testId} />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 2, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</Label>
      <AutoTextarea value={value} onChange={onChange} minRows={rows}
        className="w-full mt-1 bg-[#102A3D] border border-[#1F4B6B] text-zinc-100 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
        data-testid={testId} />
    </div>
  );
}

function FocusAreaDialog({ dialog, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (dialog?.mode === "edit") setName(dialog.item?.name || "");
    else setName("");
  }, [dialog]);
  if (!dialog) return null;
  const isEdit = dialog.mode === "edit";

  const save = async () => {
    setSaving(true);
    try {
      const url = isEdit ? `/focus-areas/${dialog.item.id}` : "/focus-areas";
      const method = isEdit ? "put" : "post";
      const { data } = await api[method](url, { name: name.trim() });
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };
  const del = async () => {
    setDeleting(true);
    try {
      await api.delete(`/focus-areas/${dialog.item.id}`);
      onSaved({ ...dialog.item, _deleted: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    } finally { setDeleting(false); }
  };
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Schwerpunkt bearbeiten" : "Neuer Schwerpunkt"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Team-eigene Liste</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Passspiel"
            className="bg-[#102A3D] border-[#1F4B6B] text-zinc-100 h-11 focus-visible:ring-brand"
            data-testid="ts-fa-name" />
        </div>
        <DialogFooter className="flex sm:justify-between gap-2">
          {isEdit ? (
            <Button variant="outline" onClick={del} disabled={deleting}
              className="bg-transparent border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-md"
              data-testid="ts-fa-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Löschen
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900">Abbrechen</Button>
            <Button onClick={save} disabled={saving || !name.trim()}
              className="bg-brand hover:bg-brand-light text-white" data-testid="ts-fa-save">
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
